import {
  Bars3BottomLeftIcon,
  BarsArrowDownIcon,
  BarsArrowUpIcon
} from '@heroicons/react/24/outline'
import {
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  // getPaginationRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useLayoutFillsViewport } from '../../context/layout-fill-context'
import { classNames } from '../../helpers'
import { useFiltersFromURL } from '../../hooks/use-filters-from-url'
import { useRect } from '../../hooks/use-rect'
import { useWindowDimensions } from '../../hooks/use-window-dimensions'
import FilterBar from './filter-bar'
import SelectBar from './select-bar'
import TableBody, { getEdgeGutterClass } from './table-body'

const Table = ({
  data,
  columns,
  initialState,
  globalFilterFn,
  withActions,
  selectionCallback,
  disableHover,
  disableVirtual,
  displayNumberOfEntries,
  className,
  classNameTableHeader,
  withOptions,
  disableFilter,
  columnState,
  fillHeight,
  label,
  highlightRowIds
}) => {
  const tableContainerRef = useRef(null)
  const scrollRegionRef = useRef(null)
  const { height: windowHeight } = useWindowDimensions()

  // fillHeight: die Tabelle nimmt per CSS die Resthöhe der Seite und ist der
  // einzige Scroll-Container. Nur mit fillViewport an der Route, sonst hätte
  // der Container keine Grenze und react-virtual würde jede Zeile rendern.
  const layoutFills = useLayoutFillsViewport()
  const fills = fillHeight && layoutFills

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return
    if (fillHeight && !layoutFills) {
      console.warn('Table fillHeight wirkt nur mit fillViewport an der Route')
    }
  }, [fillHeight, layoutFills])

  const {
    rect: { top: filterSelectBarTop },
    ref: filterSelectBarsRef
  } = useRect()

  const [rowSelection, setRowSelection] = useState({})
  const [columnFilters, setColumnFilters] = useState([])
  const [globalFilter, setGlobalFilter] = useState('')

  const { setFilterUrl } = useFiltersFromURL({
    columns,
    setColumnFilters
  })

  const filterFns = useMemo(() => {
    const result = {}
    columns.forEach(({ filter }) => filter && (result[filter.id] = filter.fn))

    return result
  }, [columns])

  const table = useReactTable({
    data,
    columns: columns.map((col) => ({
      ...col,
      filterFn: col.filter ? col.filter.id : undefined
    })),
    initialState,
    state: {
      columnFilters,
      globalFilter,
      rowSelection,
      // Bedingter Spread: ein explizites undefined würde die Default-Updater
      // von table-core überschreiben (options werden per Spread gemerged).
      ...(columnState
        ? {
            columnVisibility: columnState.columnVisibility,
            columnOrder: columnState.columnOrder
          }
        : {})
    },
    ...(columnState
      ? {
          onColumnVisibilityChange: columnState.setColumnVisibility,
          onColumnOrderChange: columnState.setColumnOrder
        }
      : {}),
    filterFns,
    globalFilterFn,
    onColumnFiltersChange: setColumnFilters,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues()
  })

  useEffect(() => {
    selectionCallback(table)
  }, [selectionCallback, table, rowSelection])

  const filterSelectBarHeight = filterSelectBarsRef.current
    ? filterSelectBarsRef.current.clientHeight
    : 0

  // Nur ohne fillHeight. Gemessen wird beim Mount und beim Resize, Inhalt
  // darüber, der später erscheint oder verschwindet, sieht diese Rechnung nicht.
  const marginBottom = 32

  const tableHeight =
    windowHeight - (filterSelectBarTop + filterSelectBarHeight + marginBottom)

  const filters = columns.map(({ filter }) => filter).filter(Boolean)

  return (
    <div
      id='react-table'
      className={classNames(
        'flex flex-col',
        fills && 'min-h-0 flex-1',
        className
      )}
    >
      {!disableFilter && (
        <div ref={filterSelectBarsRef}>
          <FilterBar
            table={table}
            filters={filters}
            setFilterUrl={setFilterUrl}
            globalFilter={
              globalFilterFn && {
                value: globalFilter,
                setValue: setGlobalFilter
              }
            }
            withOptions={withOptions}
            displayNumberOfEntries={displayNumberOfEntries}
          />
          <SelectBar table={table} withActions={withActions} />
        </div>
      )}
      {/* Mit fillHeight ist relative Pflicht. Absolut positionierter
          Zellinhalt (z. B. sr-only-Text) hinge sonst am Viewport statt am
          Scroll-Container und würde die Seite verlängern. Ohne fillHeight
          bleibt der Container unpositioniert, damit Tooltips in Zellen über
          den Tabellenrand hinausragen dürfen. */}
      <div
        ref={scrollRegionRef}
        role={fills ? 'region' : undefined}
        aria-label={fills ? label : undefined}
        tabIndex={fills ? 0 : undefined}
        className={classNames(
          'overflow-x-auto',
          fills &&
            'relative min-h-[15rem] flex-1 overflow-y-auto focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primaryBlue'
        )}
      >
        <div
          ref={tableContainerRef}
          className={classNames(
            'inline-block min-w-full align-middle',
            !fills && 'md:px-2 lg:px-4'
          )}
          style={
            fills || disableVirtual
              ? undefined
              : {
                  height: tableHeight,
                  minHeight: 400,
                  overflow: 'auto'
                }
          }
        >
          <table className='min-w-full divide-y divide-gray-300'>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index, headers) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={classNameTableHeader}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={classNames(
                            'group flex items-center whitespace-nowrap',
                            header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : '',
                            header.column.columnDef.invisible
                              ? ''
                              : 'px-4 py-4',
                            fills && getEdgeGutterClass(headers, index)
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {!header.column.columnDef.invisible &&
                            header.column.getCanSort() &&
                            !header.column.getIsSorted() && (
                              <Bars3BottomLeftIcon className='ml-2 h-4 w-4 text-gray-400' />
                            )}
                          {{
                            asc: (
                              <BarsArrowUpIcon className='ml-2 h-4 w-4 text-blue-700' />
                            ),
                            desc: (
                              <BarsArrowDownIcon className='ml-2 h-4 w-4 text-blue-700' />
                            )
                          }[header.column.getIsSorted()] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <TableBody
              data={{
                table,
                tableContainerRef: fills ? scrollRegionRef : tableContainerRef
              }}
              disableHover={disableHover}
              disableVirtual={disableVirtual}
              edgeGutter={fills}
              highlightRowIds={highlightRowIds}
            />
          </table>
        </div>
      </div>
    </div>
  )
}

Table.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  initialState: PropTypes.object,
  globalFilterFn: PropTypes.func,
  withActions: PropTypes.node,
  selectionCallback: PropTypes.func,
  disableHover: PropTypes.bool,
  disableVirtual: PropTypes.bool,
  displayNumberOfEntries: PropTypes.bool,
  className: PropTypes.string,
  classNameTableHeader: PropTypes.string,
  withOptions: PropTypes.node,
  disableFilter: PropTypes.bool,
  fillHeight: PropTypes.bool,
  label: PropTypes.string,
  // IDs (row.original.id) frisch angelegter Zeilen, leuchten einmal kurz auf
  highlightRowIds: PropTypes.arrayOf(PropTypes.string),
  columnState: PropTypes.shape({
    columnVisibility: PropTypes.object.isRequired,
    columnOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
    setColumnVisibility: PropTypes.func.isRequired,
    setColumnOrder: PropTypes.func.isRequired
  })
}

Table.defaultProps = {
  initialState: undefined,
  globalFilterFn: undefined,
  withActions: undefined,
  selectionCallback: () => {},
  disableHover: false,
  disableVirtual: false,
  displayNumberOfEntries: false,
  className: 'bg-white',
  // z-[1] hält die Kopfzeile über relativ positionierten Zellinhalten
  classNameTableHeader: 'sticky top-0 z-[1] bg-white',
  withOptions: null,
  disableFilter: false,
  columnState: undefined,
  fillHeight: false,
  label: undefined,
  highlightRowIds: []
}

export default Table
